import { useEffect } from "react";
import { Output } from "valibot";

import * as events from "shared/events";

import { BotInfoSchema, StarterQuestionsSchema } from "./schemas";
import { UpdateBotInfoConfig } from "./types";

type EventType = (typeof events)[keyof typeof events];

type CustomEventListener = (evt: CustomEvent) => void;

function useEvents(
  botInfo: Output<typeof BotInfoSchema>,
  token: string,
  updateBotInfoConfig: UpdateBotInfoConfig,
  setChatId: React.Dispatch<React.SetStateAction<string>>,
  setStarterQuestions: (
    value: React.SetStateAction<Output<typeof StarterQuestionsSchema>>,
  ) => void,
  setChatUserFormFieldsUpdateTime: (value: number) => void,
) {
  useEffect(() => {
    const eventListeners: [EventType, CustomEventListener][] = [
      [
        events.CHANGE_COLOR_EVENT,
        (evt: CustomEvent<string>) =>
          updateBotInfoConfig("chatbot_color", evt.detail),
      ],
      [
        events.CHANGE_COMPANY_NAME_EVENT,
        (evt: CustomEvent<string>) =>
          updateBotInfoConfig("company_name", evt.detail),
      ],
      [
        events.CHANGE_COMPANY_LOGO_EVENT,
        (evt: CustomEvent<string>) =>
          updateBotInfoConfig("company_logo", evt.detail),
      ],
      [
        events.CHANGE_BOT_AVATAR_EVENT,
        (evt: CustomEvent<string>) =>
          updateBotInfoConfig("chatbot_avatar", evt.detail),
      ],
      [
        events.CHANGE_CHATBOT_SUBHEADING_EVENT,
        (evt: CustomEvent<string>) =>
          updateBotInfoConfig("chatbot_subheading", evt.detail),
      ],
      [
        events.CHANGE_WELCOME_MESSAGE,
        (evt: CustomEvent<string>) =>
          updateBotInfoConfig("welcome_message", evt.detail),
      ],
      [
        events.CHANGE_SHOW_SOURCES,
        (evt: CustomEvent<boolean>) =>
          updateBotInfoConfig("is_show_sources", evt.detail),
      ],
      [
        events.CHANGE_STARTER_QUESTIONS_EVENT,
        (e: any) => {
          setStarterQuestions(Object.values(e.detail));
        },
      ],
      [
        events.CHANGE_SHOW_BRAND_EVENT,
        (evt: CustomEvent<boolean>) =>
          updateBotInfoConfig("is_show_chat_sonic_brand", evt.detail),
      ],
      [
        events.CHANGE_BUTTON_ICON_EVENT,
        (evt: CustomEvent<string>) =>
          updateBotInfoConfig("button_icon", evt.detail),
      ],
      [
        events.CREATE_CONVERSATION_ID,
        (evt: CustomEvent<string>) => setChatId(evt.detail),
      ],
      [
        events.INTERNAL_PORTAL_EVENT,
        (evt: CustomEvent<{ type: string; value: string | boolean }>) => {
          const { detail } = evt;
          if (!detail || typeof detail !== "object") {
            return;
          }
          const { type, value } = detail;
          if (type === "hide-user-form" && typeof value === "boolean") {
            updateBotInfoConfig("hide_user_form", value);
          }
        },
      ],
      [
        events.UPDATE_USER_FORM,
        () => setChatUserFormFieldsUpdateTime(Date.now()),
      ],
      [
        events.CHANGE_CUSTOM_PLACEHOLDER_EVENT,
        (evt: CustomEvent<string>) =>
          updateBotInfoConfig("custom_placeholder", evt.detail),
      ],
      [
        events.CHANGE_USER_FORM_VISIBILITY,
        (evt: CustomEvent<boolean>) =>
          updateBotInfoConfig("hide_user_form", evt.detail),
      ],
      [
        events.CHANGE_SERVER_ERROR_MESSAGE_EVENT,
        (e: any) => updateBotInfoConfig("server_error_message", e.detail),
      ],
      [
        events.CHANGE_NO_SOURCES_ADDED_MESSAGE_EVENT,
        (e: any) => updateBotInfoConfig("no_sources_message", e.detail),
      ],
    ];

    for (const [eventType, listener] of eventListeners) {
      window.addEventListener(eventType, listener);
    }

    return () => {
      for (const [eventType, listener] of eventListeners) {
        window.removeEventListener(eventType, listener);
      }
    };
  }, [
    token,
    updateBotInfoConfig,
    setChatId,
    setStarterQuestions,
    setChatUserFormFieldsUpdateTime,
  ]);

  return {
    botInfo,
  };
}

export default useEvents;
